import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  RefreshCw, Upload, Search, Package, Clock, CheckCircle2, Zap,
  Download, FileText, FileSpreadsheet, Printer, Archive, Wrench, Trash2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

/**
 * Pending Parcels sub-panel — daily operational report for partners
 * and clients. Uploads a full Order Sheet, compares against Database
 * A at product level, and lists parcels not yet packed.
 */
export const PendingParcelsPanel = () => {
  const [stats, setStats] = useState({ active: 0, over_7_days: 0, resolved_today: 0, last_sync_at: "" });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [clientCode, setClientCode] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const fileRef = useRef(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearPassword, setClearPassword] = useState("");
  const [clearBusy, setClearBusy] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [commentTemplates, setCommentTemplates] = useState([]);
  const [templateMenuOpen, setTemplateMenuOpen] = useState(false);
  // Iter-125 sort state — clicking a header cycles asc → desc → clear.
  const [sortKey, setSortKey] = useState(null);       // "order_date_iso" | "invoice_no" | "service" | "reason" | "days_pending" | "comment"
  const [sortDir, setSortDir] = useState("asc");      // "asc" | "desc"
  const cycleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortKey(null); setSortDir("asc");
  };

  // Iter-126: KPI card acts as a quick filter. Values:
  //   "" (none) | "active" | "over_7" | "resolved_today"
  const [kpiFilter, setKpiFilter] = useState("");

  const fetchStats = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (clientCode) p.set("client_code", clientCode);
      if (dateFrom)   p.set("date_from", dateFrom);
      if (dateTo)     p.set("date_to", dateTo);
      const r = await axios.get(`${API}/database-a/pending-parcels/stats?${p}`, { headers: getAuthHeader() });
      setStats(r.data);
    } catch {}
  }, [clientCode, dateFrom, dateTo]);

  const fetchRows = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      // Iter-126/127: KPI card filters override the status filter.
      let effectiveStatus = statusFilter;
      let extra = {};
      if (kpiFilter === "active")         { effectiveStatus = "pending"; }
      else if (kpiFilter === "over_7")    { effectiveStatus = "pending"; extra.min_days = 8; }
      else if (kpiFilter === "resolved_today") { effectiveStatus = "resolved"; extra.resolved_on = new Date().toISOString().slice(0, 10); }
      else if (kpiFilter === "cancelled_month") {
        effectiveStatus = "archived";
        const d = new Date();
        extra.date_from = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
        extra.reason = "cancel";
      }
      const params = new URLSearchParams({ page: p, limit: 100, status: effectiveStatus, ...extra });
      if (search) params.set("search", search);
      if (clientCode) params.set("client_code", clientCode);
      if (reasonFilter) params.set("reason", reasonFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const r = await axios.get(`${API}/database-a/pending-parcels?${params}`, { headers: getAuthHeader() });
      setRows(r.data.records || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
      setPage(r.data.page || 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pending parcels");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, kpiFilter, search, clientCode, reasonFilter, dateFrom, dateTo]);

  useEffect(() => { fetchStats(); fetchRows(1); }, [fetchStats, fetchRows]);

  // Iter-121: pull comment templates once on mount; falls back to
  // the constant defaults if the endpoint 404s (older backends).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/database-a/pending-parcels/comment-templates`,
          { headers: getAuthHeader() },
        );
        if (!cancelled) setCommentTemplates(r.data.templates || []);
      } catch { /* silently ignore — dropdown just hides */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("order_sheet", file);
      const r = await axios.post(`${API}/database-a/pending-parcels/upload`, fd, {
        headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      toast.success(`Uploaded ${r.data.rows_uploaded} order rows · ${r.data.sync_stats?.pending || 0} pending, ${r.data.sync_stats?.resolved || 0} resolved`);
      fetchStats(); fetchRows(1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/database-a/pending-parcels/sync`, {}, { headers: getAuthHeader() });
      toast.success(`Sync complete · ${r.data.pending} pending, ${r.data.resolved} resolved`);
      fetchStats(); fetchRows(page);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  // ---- Exports & printable view (iter-118 popup workflow) ---------------
  const [exportOpen, setExportOpen] = useState(false);
  const [exportMode, setExportMode] = useState("all");         // "all" | "selected"
  const [exportKind, setExportKind] = useState("excel");       // "excel" | "pdf" | "print"
  const [clientList, setClientList] = useState([]);            // [{client_code, client_name, pending_count}]
  const [clientSearch, setClientSearch] = useState("");
  const [selectedCodes, setSelectedCodes] = useState(() => new Set());
  const [clientListLoading, setClientListLoading] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);

  // Params shared by clients-list, bulk-zip, and printable view — all
  // exports must respect the active toolbar filters.
  const buildFilterParams = () => {
    const p = new URLSearchParams({ status: statusFilter });
    if (search)       p.set("search", search);
    if (reasonFilter) p.set("reason", reasonFilter);
    if (dateFrom)     p.set("date_from", dateFrom);
    if (dateTo)       p.set("date_to", dateTo);
    return p;
  };

  const openExportDialog = async (kind) => {
    setExportKind(kind);
    setExportMode("all");
    setSelectedCodes(new Set());
    setClientSearch("");
    setExportOpen(true);
    setClientListLoading(true);
    try {
      const r = await axios.get(
        `${API}/database-a/pending-parcels/clients-list?${buildFilterParams()}`,
        { headers: getAuthHeader() },
      );
      setClientList(r.data.clients || []);
    } catch (e) {
      toast.error("Could not load clients list");
      setClientList([]);
    } finally {
      setClientListLoading(false);
    }
  };

  const filteredClientList = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clientList;
    return clientList.filter((c) =>
      c.client_code.toLowerCase().includes(q) ||
      (c.client_name || "").toLowerCase().includes(q));
  }, [clientList, clientSearch]);

  const toggleClient = (code) => {
    setSelectedCodes((prev) => {
      const nxt = new Set(prev);
      if (nxt.has(code)) nxt.delete(code); else nxt.add(code);
      return nxt;
    });
  };
  const selectAllFiltered = () => setSelectedCodes(new Set(filteredClientList.map((c) => c.client_code)));
  const deselectAll       = () => setSelectedCodes(new Set());

  const codesToExport = () => (
    exportMode === "selected" ? [...selectedCodes] : clientList.map((c) => c.client_code)
  );

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const runBulkExport = async (fmt) => {
    const codes = codesToExport();
    if (exportMode === "selected" && codes.length === 0) {
      toast.error("Tick at least one client to export");
      return;
    }
    setExportBusy(true);
    try {
      const p = buildFilterParams();
      p.set("fmt", fmt);
      // Empty string means "every client with matching rows" on the
      // backend — that maps to the All Clients option and avoids a
      // 8k-char URL when there are lots of clients.
      p.set("client_codes", exportMode === "selected" ? codes.join(",") : "");
      const resp = await axios.get(
        `${API}/database-a/pending-parcels/export/bulk-zip?${p}`,
        { headers: getAuthHeader(), responseType: "blob" },
      );
      const cd = resp.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const fname = m ? m[1] : `Pending-Parcels-${fmt}.zip`;
      downloadBlob(resp.data, fname);
      toast.success(`Downloaded ${fname}${codes.length ? ` · ${codes.length} client${codes.length>1?'s':''}` : ''}`);
      setExportOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Export failed`);
    } finally {
      setExportBusy(false);
    }
  };

  const runPrint = async () => {
    const codes = codesToExport();
    if (exportMode === "selected" && codes.length === 0) {
      toast.error("Tick at least one client to print");
      return;
    }
    setExportBusy(true);
    try {
      // Fetch full row set matching current filters — same query the
      // list uses, but capped high so a printable view carries the
      // full report.
      const p = buildFilterParams();
      p.set("limit", "5000");
      p.set("page", "1");
      const r = await axios.get(
        `${API}/database-a/pending-parcels?${p}`,
        { headers: getAuthHeader() },
      );
      const all = r.data.records || [];
      const codeSet = new Set(codes.map((c) => c.toUpperCase()));
      const chosen = exportMode === "all" ? all
                                          : all.filter((row) => codeSet.has((row.client_code || "").toUpperCase()));
      if (chosen.length === 0) {
        toast.error("No rows to print for the current selection");
        return;
      }
      openPrintWindow(chosen);
      setExportOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Print failed");
    } finally {
      setExportBusy(false);
    }
  };

  const openPrintWindow = (allRows) => {
    // Iter-127: instead of triggering window.print() immediately, we
    // now render a toolbar-driven viewer (Print · PDF · Excel · Close).
    // The child window inherits our origin so it can read the JWT
    // from localStorage and call the same bulk-zip / list endpoints
    // for downloads.
    const groups = {};
    for (const row of allRows) {
      const cc = (row.client_code || "—").toUpperCase();
      (groups[cc] = groups[cc] || []).push(row);
    }
    const orderedCodes = Object.keys(groups).sort();
    const nowStr = new Date().toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
    const win = window.open("", "_blank", "width=1200,height=900");
    if (!win) { toast.error("Popup blocked — allow popups to print"); return; }

    // Params carried to the child so its toolbar buttons can call
    // the exact same-filter export endpoints as the panel.
    const exportParams = buildFilterParams().toString();
    const codesJoin = orderedCodes.join(",");

    const clientSection = (cc, list) => {
      const bodyRows = list.map((r) => `
        <tr class="${r.status === 'resolved' ? 'resolved' : ((r.days_pending||0) > 14 ? 'red' : (r.days_pending||0) > 7 ? 'amber' : '')}">
          <td>${r.order_date || ''}</td>
          <td>${(r.customer_name || '').replace(/</g, '&lt;')}</td>
          <td><strong>${(r.product || '').replace(/</g, '&lt;')}</strong></td>
          <td class="num">${r.qty || 0}</td>
          <td class="num">${r.ordered_count || 0}</td>
          <td class="num">${r.packed_count || 0}</td>
          <td class="num"><strong>${r.pending_count || 0}</strong></td>
          <td>${(r.reason || '').replace(/</g, '&lt;')}</td>
          <td class="mono">${r.tracking || ''}</td>
          <td class="num">${r.days_pending || 0}</td>
          <td class="status">${(r.status || '').toUpperCase()}</td>
        </tr>`).join("");
      return `
        <section class="client-block">
          <div class="hd">
            <h2>Pending Parcels &middot; ${cc}</h2>
            <div class="sub">${list.length} rows &middot; Generated ${nowStr}</div>
          </div>
          <table>
            <thead><tr>
              <th>Date</th><th>Customer</th><th>Product</th>
              <th>Qty</th><th>Ord</th><th>Pkd</th><th>Pend</th>
              <th>Reason</th><th>Tracking</th><th>Days</th><th>Status</th>
            </tr></thead>
            <tbody>${bodyRows}</tbody>
          </table>
        </section>`;
    };

    win.document.write(`<!doctype html><html><head><title>Pending Parcels · Report</title>
      <style>
        body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;margin:0;color:#0f172a;background:#f8fafc}
        .toolbar{position:sticky;top:0;z-index:20;display:flex;gap:8px;align-items:center;padding:10px 18px;background:#1e293b;color:#fff;box-shadow:0 1px 3px rgba(0,0,0,.15)}
        .toolbar h1{font-size:14px;margin:0;font-weight:600}
        .toolbar .flex-grow{flex:1}
        .toolbar button{border:none;font:inherit;padding:7px 12px;border-radius:5px;cursor:pointer;font-size:12px;font-weight:600;letter-spacing:.02em;display:inline-flex;align-items:center;gap:6px}
        .toolbar button.pdf{background:#e11d48;color:#fff}
        .toolbar button.pdf:hover{background:#be123c}
        .toolbar button.xls{background:#10b981;color:#fff}
        .toolbar button.xls:hover{background:#059669}
        .toolbar button.print{background:#4f46e5;color:#fff}
        .toolbar button.print:hover{background:#4338ca}
        .toolbar button.close{background:transparent;color:#cbd5e1;border:1px solid #475569}
        .toolbar button.close:hover{background:#334155;color:#fff}
        .content{padding:20px 24px;max-width:1500px;margin:0 auto;background:#fff}
        h2{margin:0 0 2px;font-size:16px}
        .sub{color:#64748b;font-size:11px;margin-bottom:8px}
        table{width:100%;border-collapse:collapse;font-size:10px;margin-bottom:18px}
        th{background:#1e293b;color:#fff;padding:6px 5px;text-align:left;font-size:9.5px;letter-spacing:.02em}
        td{padding:5px;border-top:1px solid #e2e8f0;vertical-align:top}
        td.num{text-align:right;font-variant-numeric:tabular-nums}
        td.mono{font-family:ui-monospace,monospace;color:#475569;font-size:9.5px}
        td.status{font-weight:600;text-align:center}
        tr.amber td{background:#fffbeb}
        tr.red td{background:#fef2f2}
        tr.resolved td{color:#94a3b8;text-decoration:line-through;font-style:italic}
        .client-block{page-break-before:always}
        .client-block:first-of-type{page-break-before:auto}
        @media print{
          .toolbar{display:none}
          .content{padding:0}
          @page{size:A4 landscape;margin:8mm}
          body{background:#fff}
        }
      </style></head><body>
      <div class="toolbar" data-testid="pp-print-toolbar">
        <h1>Pending Parcels Report · ${allRows.length} rows · ${orderedCodes.length} client${orderedCodes.length !== 1 ? 's' : ''}</h1>
        <div class="flex-grow"></div>
        <button class="pdf"   onclick="ppDownload('pdf')"   data-testid="pp-print-pdf">Export PDF</button>
        <button class="xls"   onclick="ppDownload('excel')" data-testid="pp-print-excel">Export Excel</button>
        <button class="print" onclick="window.print()"      data-testid="pp-print-print">Print</button>
        <button class="close" onclick="window.close()"      data-testid="pp-print-close">Close</button>
      </div>
      <div class="content">
        ${orderedCodes.map((cc) => clientSection(cc, groups[cc])).join("")}
      </div>
      <script>
        const API = ${JSON.stringify(API)};
        const TOKEN = localStorage.getItem('token') || '';
        const PARAMS = ${JSON.stringify(exportParams)};
        const CODES  = ${JSON.stringify(codesJoin)};
        async function ppDownload(fmt) {
          try {
            const p = new URLSearchParams(PARAMS);
            p.set('fmt', fmt);
            p.set('client_codes', CODES);
            const url = API + '/database-a/pending-parcels/export/bulk-zip?' + p.toString();
            const r = await fetch(url, { headers: { Authorization: 'Bearer ' + TOKEN } });
            if (!r.ok) { alert('Download failed (' + r.status + ')'); return; }
            const cd = r.headers.get('content-disposition') || '';
            const m = cd.match(/filename="([^"]+)"/);
            const fname = m ? m[1] : 'Pending-Parcels.zip';
            const blob = await r.blob();
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = fname;
            document.body.appendChild(a);
            a.click();
            a.remove();
          } catch (e) { alert('Download error: ' + (e && e.message)); }
        }
      </script>
    </body></html>`);
    win.document.close();
  };

  const handleArchiveNow = async () => {
    if (!window.confirm("Move all rows older than 5 months to the archive?")) return;
    try {
      const r = await axios.post(`${API}/database-a/pending-parcels/archive-now`, {}, { headers: getAuthHeader() });
      toast.success(`Archived ${r.data.archived} rows (cutoff ${r.data.cutoff})`);
      fetchStats(); fetchRows(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    }
  };

  const handleRebuild = async () => {
    setRebuilding(true);
    try {
      const r = await axios.post(`${API}/database-a/pending-parcels/rebuild`, {}, { headers: getAuthHeader() });
      toast.success(`Rebuilt · ${r.data.pending} pending, ${r.data.resolved} resolved (from ${r.data.total} rows)`);
      fetchStats(); fetchRows(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rebuild failed");
    } finally {
      setRebuilding(false);
    }
  };

  const handleClearUploads = async () => {
    setClearBusy(true);
    try {
      const r = await axios.post(
        `${API}/database-a/pending-parcels/clear-uploads`,
        { password: clearPassword },
        { headers: getAuthHeader() },
      );
      toast.success(`Cleared ${r.data.deleted_live} live + ${r.data.deleted_archive} archived rows`);
      setClearOpen(false);
      setClearPassword("");
      fetchStats(); fetchRows(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally {
      setClearBusy(false);
    }
  };

  const beginEditComment = (row) => {
    setEditingCommentId(row.id);
    setCommentDraft(row.comment || "");
    setTemplateMenuOpen(false);
  };
  const saveComment = async (row) => {
    const value = commentDraft.trim();
    setEditingCommentId(null);
    setTemplateMenuOpen(false);
    if (value === (row.comment || "")) return;   // no change
    try {
      await axios.put(
        `${API}/database-a/pending-parcels/${row.id}/comment`,
        { comment: value },
        { headers: getAuthHeader() },
      );
      // Optimistic local update — avoid a round-trip
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, comment: value } : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save comment");
    }
  };

  const lastSyncDisplay = stats.last_sync_at
    ? new Date(stats.last_sync_at).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })
    : "—";

  return (
    <div className="space-y-4" data-testid="pending-parcels-panel">
      {/* KPI cards — iter-126 clickable filters + iter-127 cancelled counter */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <button type="button"
          onClick={() => setKpiFilter((v) => v === "active" ? "" : "active")}
          className={`text-left rounded-lg transition ${kpiFilter === 'active' ? 'ring-2 ring-amber-400' : 'hover:ring-1 hover:ring-slate-200'}`}
          data-testid="pp-kpi-active-btn">
          <KpiCard icon={<Package className="w-4 h-4" />} label="Active Pending"
            value={stats.active} tone="amber" testid="pp-kpi-active"
            subtitle={stats.active_rows > stats.active ? `${stats.active_rows} rows across ${stats.active} parcels` : "Parcels"} />
        </button>
        <button type="button"
          onClick={() => setKpiFilter((v) => v === "over_7" ? "" : "over_7")}
          className={`text-left rounded-lg transition ${kpiFilter === 'over_7' ? 'ring-2 ring-rose-400' : 'hover:ring-1 hover:ring-slate-200'}`}
          data-testid="pp-kpi-over7-btn">
          <KpiCard icon={<Clock className="w-4 h-4" />} label="Over 7 Days"
            value={stats.over_7_days} tone="rose" testid="pp-kpi-aging"
            subtitle="Parcels aging" />
        </button>
        <button type="button"
          onClick={() => setKpiFilter((v) => v === "resolved_today" ? "" : "resolved_today")}
          className={`text-left rounded-lg transition ${kpiFilter === 'resolved_today' ? 'ring-2 ring-emerald-400' : 'hover:ring-1 hover:ring-slate-200'}`}
          data-testid="pp-kpi-resolved-btn">
          <KpiCard icon={<CheckCircle2 className="w-4 h-4" />} label="Resolved Today"
            value={stats.resolved_today} tone="emerald" testid="pp-kpi-resolved"
            subtitle="Parcels resolved" />
        </button>
        <button type="button"
          onClick={() => setKpiFilter((v) => v === "cancelled_month" ? "" : "cancelled_month")}
          className={`text-left rounded-lg transition ${kpiFilter === 'cancelled_month' ? 'ring-2 ring-slate-500' : 'hover:ring-1 hover:ring-slate-200'}`}
          data-testid="pp-kpi-cancelled-btn">
          <KpiCard icon={<Archive className="w-4 h-4" />} label="Cancelled This Month"
            value={stats.cancelled_this_month || 0} tone="slate" testid="pp-kpi-cancelled"
            subtitle="Parcels archived" />
        </button>
        <KpiCard icon={<Zap className="w-4 h-4" />} label="Last Sync"
          value={lastSyncDisplay} tone="slate" testid="pp-kpi-sync" small />
      </div>
      {kpiFilter && (
        <div className="flex items-center gap-2 text-xs text-slate-600 -mt-1">
          <span className="font-medium">Filtered by KPI:</span>
          <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">
            {kpiFilter === 'active' ? 'Active Pending'
              : kpiFilter === 'over_7' ? 'Over 7 Days'
              : kpiFilter === 'cancelled_month' ? 'Cancelled This Month'
              : 'Resolved Today'}
          </span>
          <button className="underline text-slate-500 hover:text-slate-800"
                  onClick={() => setKpiFilter("")} data-testid="pp-kpi-clear">
            clear
          </button>
        </div>
      )}

      {/* Toolbar */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.csv"
            onChange={handleUpload} className="hidden"
            data-testid="pp-upload-input" />
          <Button onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="pp-upload-btn">
            <Upload className="w-4 h-4 mr-1.5" />
            {uploading ? "Uploading…" : "Upload Full Order Sheet"}
          </Button>
          <Button variant="outline" onClick={handleSync} disabled={syncing}
            data-testid="pp-sync-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
            Sync Now
          </Button>
          <Button variant="outline" onClick={() => openExportDialog("excel")}
            data-testid="pp-export-btn">
            <Download className="w-4 h-4 mr-1.5" />
            Export
          </Button>
          <Button variant="outline" onClick={() => openExportDialog("print")}
            data-testid="pp-print-btn">
            <Printer className="w-4 h-4 mr-1.5" />
            Screen Print
          </Button>
          <Button variant="outline" onClick={handleArchiveNow}
            title="Move rows older than 5 months to the archive collection"
            data-testid="pp-archive-btn">
            <Archive className="w-4 h-4 mr-1.5" />
            Archive
            {stats.archived_count > 0 && (
              <span className="ml-1.5 text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">
                {stats.archived_count}
              </span>
            )}
          </Button>
          <Button variant="outline" onClick={handleRebuild} disabled={rebuilding}
            title="Recalculate packed/pending status without deleting anything"
            data-testid="pp-rebuild-btn">
            <Wrench className={`w-4 h-4 mr-1.5 ${rebuilding ? 'animate-spin' : ''}`} />
            {rebuilding ? "Rebuilding…" : "Rebuild Pending List"}
          </Button>
          <Button variant="outline"
            onClick={() => { setClearPassword(""); setClearOpen(true); }}
            title="Delete uploaded order-sheet rows (Database A untouched)"
            className="text-rose-600 hover:text-rose-700 border-rose-200"
            data-testid="pp-clear-btn">
            <Trash2 className="w-4 h-4 mr-1.5" />
            Clear Uploaded Order Sheet
          </Button>
          <div className="flex-1" />
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input placeholder="Search parcel…" value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchRows(1)}
              className="pl-8 h-8 text-xs w-56"
              data-testid="pp-search-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 text-xs w-32" data-testid="pp-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending only</SelectItem>
              <SelectItem value="resolved">Resolved only</SelectItem>
              <SelectItem value="all">All (history)</SelectItem>
              <SelectItem value="archived">
                <span className="flex items-center gap-1.5">
                  <Archive className="w-3 h-3" /> Archived
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Client code" value={clientCode}
            onChange={(e) => setClientCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && fetchRows(1)}
            className="h-8 text-xs w-28" data-testid="pp-client-filter" />
          <Input placeholder="Reason" value={reasonFilter}
            onChange={(e) => setReasonFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchRows(1)}
            className="h-8 text-xs w-32" data-testid="pp-reason-filter" />
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="h-8 text-xs w-36" data-testid="pp-date-from" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="h-8 text-xs w-36" data-testid="pp-date-to" />
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse" data-testid="pp-table">
              <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wide">
                <tr>
                  {(() => {
                    // Iter-125 sortable header renderer.
                    const SortHead = ({ label, k, align = "left", extra = null }) => {
                      const active = sortKey === k;
                      const arrow = !active ? "" : sortDir === "asc" ? " ▲" : " ▼";
                      return (
                        <th className={`text-${align} px-2 py-2 border border-slate-200 cursor-pointer select-none hover:bg-slate-200/60 ${active ? 'bg-slate-200/50 text-slate-800' : ''}`}
                            onClick={() => cycleSort(k)}
                            data-testid={`pp-sort-${k}`}>
                          {label}{arrow}
                          {extra}
                        </th>
                      );
                    };
                    return (
                      <>
                        <SortHead label="Date"      k="order_date_iso" />
                        <th className="text-left px-2 py-2 border border-slate-200">Client</th>
                        <SortHead label="Invoice #" k="invoice_no" />
                        <th className="text-left px-2 py-2 border border-slate-200">Customer</th>
                        <SortHead label="Service"   k="service" />
                        <th className="text-left px-2 py-2 border border-slate-200">Product</th>
                        <th className="text-right px-2 py-2 border border-slate-200">Qty</th>
                        <th className="text-center px-2 py-2 border border-slate-200">Status</th>
                        <SortHead label="Reason"    k="reason" />
                        <th className="text-left px-2 py-2 border border-slate-200">Tracking</th>
                        <SortHead label="Days"      k="days_pending" align="right" />
                        <SortHead label="Comment"   k="comment" extra={
                          <span className="ml-1 text-[9px] font-normal normal-case text-slate-400">(dbl-click)</span>
                        } />
                      </>
                    );
                  })()}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={12} className="text-center p-6 text-slate-500">Loading…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={12} className="text-center p-8">
                    <div className="text-slate-400 flex flex-col items-center gap-2">
                      <Package className="w-8 h-8" />
                      <div className="text-sm">No pending parcels. Upload an Order Sheet to get started.</div>
                    </div>
                  </td></tr>
                )}
                {!loading && loading === false && (() => {
                  // Iter-125: sort → then group by parcel key
                  // (date + client + customer + invoice). Tracking
                  // and service render only on the FIRST row of the
                  // group. Each group gets an alternating tint and
                  // a thicker top-border on its first row so the
                  // next parcel is instantly distinguishable.
                  const parcelKey = (r) => [
                    r.order_date_iso || '', (r.client_code || '').toUpperCase(),
                    (r.customer_name || '').trim().toLowerCase(),
                    (r.invoice_no || '').toUpperCase(),
                  ].join('|');

                  // 1. Bucket rows into parcel groups (preserves order)
                  const groups = [];
                  const groupIx = {};
                  for (const row of rows) {
                    const k = parcelKey(row);
                    if (!(k in groupIx)) { groupIx[k] = groups.length; groups.push({ key: k, rows: [] }); }
                    groups[groupIx[k]].rows.push(row);
                  }

                  // 2. Sort GROUPS by selected key using first-row value.
                  const cmp = (a, b) => {
                    if (a == null && b == null) return 0;
                    if (a == null) return 1;
                    if (b == null) return -1;
                    if (typeof a === 'number' && typeof b === 'number') return a - b;
                    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
                  };
                  if (sortKey) {
                    const dir = sortDir === 'desc' ? -1 : 1;
                    groups.sort((g1, g2) => dir * cmp(g1.rows[0][sortKey], g2.rows[0][sortKey]));
                  }

                  // 3. Render flat rows with group-aware first-row logic.
                  const out = [];
                  groups.forEach((g, gIdx) => {
                    const isEvenGroup = gIdx % 2 === 0;
                    const groupTint   = isEvenGroup ? '' : 'bg-amber-50/40';
                    g.rows.forEach((r, rIdx) => {
                      const isFirstInGroup = rIdx === 0;
                      const isResolved = r.status === 'resolved';
                      const isEditing  = editingCommentId === r.id;
                      const partial    = (r.packed_count || 0) > 0 && r.pending_count > 0;
                      const rowCls = isResolved
                        ? 'text-slate-400 line-through bg-slate-50'
                        : `text-slate-800 ${groupTint}`;
                      const topBorder = isFirstInGroup ? 'border-t-2 border-t-slate-400' : '';
                      out.push(
                      <tr key={r.id} className={`${rowCls} ${topBorder}`}
                          data-testid={`pp-row-${r.id}`} data-parcel-key={g.key}
                          data-parcel-first={isFirstInGroup ? 'true' : 'false'}>
                        <td className={`px-2 py-1.5 whitespace-nowrap border border-slate-200 ${isFirstInGroup ? 'font-semibold' : 'text-slate-400'}`}>
                          {isFirstInGroup ? (r.order_date || '—') : ''}
                        </td>
                        <td className={`px-2 py-1.5 font-mono text-[10px] border border-slate-200 ${isFirstInGroup ? '' : 'text-slate-300'}`}>
                          {isFirstInGroup ? r.client_code : ''}
                        </td>
                        <td className={`px-2 py-1.5 font-mono text-[10px] border border-slate-200 ${isFirstInGroup ? 'font-semibold' : 'text-slate-300'}`}>
                          {isFirstInGroup ? (r.invoice_no || <span className="text-slate-400">—</span>) : ''}
                        </td>
                        <td className={`px-2 py-1.5 border border-slate-200 ${isFirstInGroup ? 'font-medium' : 'text-slate-300'}`}>
                          {isFirstInGroup ? r.customer_name : ''}
                        </td>
                        <td className="px-2 py-1.5 text-[10px] border border-slate-200">
                          {isFirstInGroup && r.service ? (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-semibold">
                              {r.service}
                            </span>
                          ) : ''}
                        </td>
                        <td className="px-2 py-1.5 font-medium border border-slate-200">{r.product}</td>
                        <td className="px-2 py-1.5 text-right border border-slate-200">
                          {partial ? (
                            <span title={`${r.ordered_count} ordered, ${r.packed_count} packed, ${r.pending_count} pending`}
                                  className="inline-flex items-center gap-1">
                              <strong>{r.pending_count}</strong>
                              <span className="text-[9px] text-slate-500">/ {r.ordered_count}</span>
                            </span>
                          ) : (
                            <span>{r.qty} {r.ordered_count > 1 && (<span className="text-[9px] text-slate-500">×{r.ordered_count}</span>)}</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-center border border-slate-200">
                          {isResolved ? (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">RESOLVED</span>
                          ) : (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">PENDING</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 border border-slate-200">
                          {r.reason || (isResolved ? '' : <em className="text-slate-400">Pending – No Reason</em>)}
                        </td>
                        <td className="px-2 py-1.5 font-mono text-[10px] border border-slate-200">
                          {isFirstInGroup && r.tracking ? (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold">
                              {r.tracking}
                            </span>
                          ) : isFirstInGroup ? (
                            <span className="text-slate-400">—</span>
                          ) : ''}
                        </td>
                        <td className={`px-2 py-1.5 text-right font-semibold border border-slate-200 ${
                          (r.days_pending || 0) > 14 ? 'text-rose-600' :
                          (r.days_pending || 0) > 7 ? 'text-amber-600' : 'text-slate-600'
                        }`}>
                          {isFirstInGroup ? (r.days_pending || 0) : ''}
                        </td>
                      <td
                        onDoubleClick={() => !isEditing && beginEditComment(r)}
                        className="px-2 py-1 border border-slate-200 align-top cursor-text hover:bg-amber-50/40"
                        title="Double-click to edit"
                        data-testid={`pp-comment-cell-${r.id}`}
                      >
                        {isEditing ? (
                          <div className="relative">
                            <textarea
                              autoFocus
                              value={commentDraft}
                              onChange={(e) => setCommentDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') { setEditingCommentId(null); setTemplateMenuOpen(false); }
                                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { saveComment(r); }
                              }}
                              className="w-full text-xs p-1 pr-16 border border-indigo-300 rounded resize-y min-h-[52px] focus:outline-none focus:ring-1 focus:ring-indigo-400"
                              placeholder="Why is this parcel pending? (Cmd/Ctrl+Enter to save · Esc to cancel)"
                              data-testid={`pp-comment-editor-${r.id}`}
                            />
                            {commentTemplates.length > 0 && (
                              <div className="absolute top-1 right-1">
                                <button
                                  type="button"
                                  className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-medium border border-indigo-200 flex items-center gap-0.5"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => setTemplateMenuOpen((v) => !v)}
                                  data-testid={`pp-template-btn-${r.id}`}
                                >
                                  Templates ▾
                                </button>
                                {templateMenuOpen && (
                                  <div
                                    className="absolute right-0 top-6 z-50 w-64 max-h-72 overflow-y-auto bg-white shadow-lg border border-slate-200 rounded"
                                    onMouseDown={(e) => e.preventDefault()}
                                    data-testid={`pp-template-menu-${r.id}`}
                                  >
                                    <div className="text-[10px] uppercase text-slate-400 px-2 py-1 border-b bg-slate-50 tracking-wide">
                                      Quick fill · click to insert
                                    </div>
                                    {commentTemplates.map((t) => (
                                      <button
                                        key={t.label}
                                        type="button"
                                        className="w-full text-left px-2 py-1.5 text-xs hover:bg-indigo-50 border-b border-slate-50 last:border-b-0"
                                        onClick={() => {
                                          setCommentDraft(t.text);
                                          setTemplateMenuOpen(false);
                                        }}
                                        data-testid={`pp-template-${t.label.toLowerCase().replace(/\s+/g,'-')}`}
                                      >
                                        <div className="font-medium text-slate-800">{t.label}</div>
                                        <div className="text-[10px] text-slate-500 truncate">{t.text}</div>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="flex justify-end gap-1 mt-1">
                              <button
                                type="button"
                                className="text-[10px] px-2 py-0.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => { setEditingCommentId(null); setTemplateMenuOpen(false); }}
                              >Cancel</button>
                              <button
                                type="button"
                                className="text-[10px] px-2 py-0.5 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => saveComment(r)}
                                data-testid={`pp-save-comment-${r.id}`}
                              >Save</button>
                            </div>
                          </div>
                        ) : r.comment ? (
                          <div className="whitespace-pre-wrap text-slate-700">
                            {r.comment}
                            {r.comment_by && (
                              <div className="text-[9px] text-slate-400 mt-0.5">— {r.comment_by}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-300 italic text-[10px]">Double-click to add…</span>
                        )}
                      </td>
                    </tr>
                  );
                    });  // g.rows.forEach
                  });    // groups.forEach
                  return out;
                })()}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Showing page <strong>{page}</strong> of <strong>{pages}</strong> · {total} total rows</span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" disabled={page <= 1}
              onClick={() => fetchRows(page - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= pages}
              onClick={() => fetchRows(page + 1)}>Next</Button>
          </div>
        </div>
      )}

      {/* Export / Print popup — iter-118 */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-2xl" data-testid="pp-export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {exportKind === "print" ? (
                <><Printer className="w-4 h-4 text-slate-600" /> Screen Print Pending Parcels</>
              ) : (
                <><Download className="w-4 h-4 text-indigo-600" /> Export Pending Parcels</>
              )}
            </DialogTitle>
            <DialogDescription>
              Choose the scope, then pick a format below. Current toolbar filters
              (status, date, reason, search) are always applied.
            </DialogDescription>
          </DialogHeader>

          {/* Mode radio */}
          <div className="flex gap-2 border rounded-md p-1 bg-slate-50">
            <label className={`flex-1 flex items-center justify-center gap-2 py-1.5 rounded text-xs font-medium cursor-pointer transition ${exportMode === 'all' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
              <input type="radio" name="pp-scope" value="all" className="sr-only"
                     checked={exportMode === 'all'}
                     onChange={() => setExportMode('all')}
                     data-testid="pp-mode-all" />
              All Client Codes
              <span className="text-[10px] font-mono opacity-70">({clientList.length})</span>
            </label>
            <label className={`flex-1 flex items-center justify-center gap-2 py-1.5 rounded text-xs font-medium cursor-pointer transition ${exportMode === 'selected' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
              <input type="radio" name="pp-scope" value="selected" className="sr-only"
                     checked={exportMode === 'selected'}
                     onChange={() => setExportMode('selected')}
                     data-testid="pp-mode-selected" />
              Selected Client Codes
              {selectedCodes.size > 0 && (
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">
                  {selectedCodes.size} ticked
                </span>
              )}
            </label>
          </div>

          {/* Selection area */}
          {exportMode === 'selected' && (
            <div className="border rounded-md">
              <div className="flex items-center gap-2 px-2 py-1.5 border-b bg-slate-50">
                <div className="relative flex-1">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input placeholder="Search client code or name…"
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="pl-7 h-7 text-xs"
                    data-testid="pp-client-search" />
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs"
                  onClick={selectAllFiltered} data-testid="pp-select-all">
                  Select All
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs"
                  onClick={deselectAll} data-testid="pp-deselect-all">
                  Deselect All
                </Button>
              </div>
              <div className="max-h-[320px] overflow-y-auto">
                {clientListLoading && (
                  <div className="p-6 text-center text-xs text-slate-500">Loading clients…</div>
                )}
                {!clientListLoading && filteredClientList.length === 0 && (
                  <div className="p-6 text-center text-xs text-slate-500">No clients match your filters</div>
                )}
                {!clientListLoading && filteredClientList.length > 0 && (
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                      <tr>
                        <th className="w-8 text-center py-1.5">✓</th>
                        <th className="text-left py-1.5 px-2">Client Code</th>
                        <th className="text-left py-1.5 px-2">Client Name</th>
                        <th className="text-right py-1.5 px-2">Pending</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClientList.map((c) => (
                        <tr key={c.client_code}
                            className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${selectedCodes.has(c.client_code) ? 'bg-indigo-50/60' : ''}`}
                            onClick={() => toggleClient(c.client_code)}
                            data-testid={`pp-client-row-${c.client_code}`}>
                          <td className="text-center py-1.5">
                            <Checkbox
                              checked={selectedCodes.has(c.client_code)}
                              onCheckedChange={() => toggleClient(c.client_code)}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`pp-client-check-${c.client_code}`} />
                          </td>
                          <td className="py-1.5 px-2 font-mono text-[11px]">{c.client_code}</td>
                          <td className="py-1.5 px-2 text-slate-700">{c.client_name || <em className="text-slate-400">—</em>}</td>
                          <td className="py-1.5 px-2 text-right font-semibold text-amber-700">{c.pending_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {exportMode === 'all' && (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <div className="font-medium text-slate-900 mb-0.5">Every client with matching rows will be included.</div>
              <div>
                {exportKind === 'print'
                  ? 'A printable view will render each client on its own page.'
                  : 'One file per client is packaged as a ZIP — file name PP-<CLIENT>-DD MMM YYYY.<ext>.'}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setExportOpen(false)}
                    disabled={exportBusy} data-testid="pp-cancel-btn">
              Cancel
            </Button>
            {exportKind === 'print' ? (
              <Button onClick={runPrint} disabled={exportBusy}
                      className="bg-slate-800 hover:bg-slate-900 text-white"
                      data-testid="pp-run-print">
                <Printer className="w-4 h-4 mr-1.5" />
                {exportBusy ? 'Preparing…' : 'Open Printable View'}
              </Button>
            ) : (
              <>
                <Button onClick={() => runBulkExport("pdf")} disabled={exportBusy}
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        data-testid="pp-run-pdf">
                  <FileText className="w-4 h-4 mr-1.5" />
                  {exportBusy ? 'Building…' : 'Export PDF'}
                </Button>
                <Button onClick={() => runBulkExport("excel")} disabled={exportBusy}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="pp-run-excel">
                  <FileSpreadsheet className="w-4 h-4 mr-1.5" />
                  {exportBusy ? 'Building…' : 'Export Excel'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Uploaded Order Sheet — password-gated */}
      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent className="max-w-md" data-testid="pp-clear-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="w-4 h-4" /> Clear Uploaded Order Sheet
            </DialogTitle>
            <DialogDescription>
              Delete the uploaded Order Sheet rows and their generated
              pending records? <strong>Database A will not be affected.</strong>
              This wipes both the live and the archive collections.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-700">
              Enter Database A admin password to confirm
            </label>
            <Input
              type="password"
              placeholder="Admin password"
              value={clearPassword}
              onChange={(e) => setClearPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && clearPassword && handleClearUploads()}
              data-testid="pp-clear-password"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setClearOpen(false)}
                    disabled={clearBusy} data-testid="pp-clear-cancel">
              Cancel
            </Button>
            <Button onClick={handleClearUploads}
                    disabled={clearBusy || !clearPassword}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid="pp-clear-confirm">
              <Trash2 className="w-4 h-4 mr-1.5" />
              {clearBusy ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const KpiCard = ({ icon, label, value, subtitle, tone, testid, small }) => {
  const toneMap = {
    amber:   'bg-amber-50 border-amber-200 text-amber-900',
    rose:    'bg-rose-50 border-rose-200 text-rose-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    slate:   'bg-slate-50 border-slate-200 text-slate-900',
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${toneMap[tone] || toneMap.slate}`}
         data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase opacity-80">
        {icon}
        <span>{label}</span>
      </div>
      <div className={`mt-0.5 ${small ? 'text-sm' : 'text-2xl'} font-bold`}>{value}</div>
      {subtitle && <div className="text-[10px] opacity-70">{subtitle}</div>}
    </div>
  );
};

export default PendingParcelsPanel;
